import dayjs from "dayjs";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFines, useUpdateFine } from "../api/fines-api";
import { queryClient } from "../api/queryClient";
import { BankInfoSheet } from "../components/dss/BankInfoSheet";
import { Icon } from "../components/dss/Icon";
import { Eyebrow } from "../components/dss/Primitives";
import { FineDetailSheet } from "../components/dss/FineDetailSheet";
import FineRow from "../components/dss/FineRow";
import MarkPaidSheet from "../components/dss/MarkPaidSheet";
import { FadeIn, Stagger, StaggerItem, useEnterAnimation } from "../components/dss/Motion";
import { Skeleton } from "../components/dss/Skeleton";
import { useSupabaseAuth } from "../supabase/SupabaseContext";
import { Fine } from "../types/entities";

/** Fines come in batches — a meeting's worth at a time — so the list reads
 *  as one card per day rather than a column of identical dates. `fines`
 *  arrives newest first, and the groups keep that order. */
function groupByDay(fines: Fine[]) {
  const groups: { day: string; fines: Fine[]; total: number }[] = [];
  for (const fine of fines) {
    const day = dayjs(fine.created_at).format("YYYY-MM-DD");
    const last = groups[groups.length - 1];
    if (last?.day === day) {
      last.fines.push(fine);
      last.total += fine.amount_kr;
    } else {
      groups.push({ day, fines: [fine], total: fine.amount_kr });
    }
  }
  return groups;
}

function dayLabel(day: string) {
  const d = dayjs(day);
  return d.format(d.year() === dayjs().year() ? "dddd D. MMMM" : "dddd D. MMMM YYYY");
}

const ListPenaltiesPage: FC = () => {
  const { hasRole } = useSupabaseAuth();
  const { data: fines = [], isLoading } = useFines();
  const animateIn = useEnterAnimation(isLoading);
  const { mutateAsync: updateFine, isLoading: isMarking } = useUpdateFine();
  const [markingFine, setMarkingFine] = useState<Fine | null>(null);
  const [detailFine, setDetailFine] = useState<Fine | null>(null);
  const [bankInfoOpen, setBankInfoOpen] = useState(false);
  const navigate = useNavigate();

  const canEdit = hasRole("ADMIN") || hasRole("TAX_COLLECTOR");
  const totalAmount = fines.reduce((sum, f) => sum + f.amount_kr, 0);
  const unpaidAmount = fines.reduce((sum, f) => (f.status === "paid" ? sum : sum + f.amount_kr), 0);
  const paidAmount = totalAmount - unpaidAmount;
  const paidShare = totalAmount > 0 ? paidAmount / totalAmount : 0;
  const groups = useMemo(() => groupByDay(fines), [fines]);

  const onConfirmPaid = async (paidVia: Fine["paid_via"]) => {
    if (!markingFine) return;
    await updateFine({
      fineId: markingFine.id,
      updates: { status: "paid", paid_at: new Date().toISOString(), paid_via: paidVia },
    });
    queryClient.invalidateQueries(["fines"]);
    setMarkingFine(null);
  };

  return (
    <div data-scroll-container data-sticky-scroll className="flex-1 overflow-y-auto px-7">
      <div className="pt-2 flex items-start justify-between gap-3">
        <Eyebrow>SÆSONEN {new Date().getFullYear()}</Eyebrow>
        <button
          onClick={() => setBankInfoOpen(true)}
          aria-label="Bankoplysninger"
          className="-m-1.5 p-1.5 text-ink3 flex-shrink-0"
        >
          <Icon name="circle-info" size={19} />
        </button>
      </div>
      <div className="font-display text-[52px] leading-[0.9] tracking-tight font-extrabold mt-1.5 uppercase">
        Bøder<span className="text-clay">.</span>
      </div>
      <div className="mt-4 pt-4 pb-4 px-5 rounded-lg bg-ink text-paper">
        <div className="flex items-baseline justify-between gap-3">
          <div className="font-mono text-[10px] tracking-wide text-white/55 uppercase">Bøder i alt</div>
          {!isLoading && fines.length > 0 && (
            <div className="font-mono text-[10px] tracking-wide text-white/55 uppercase tabular-nums">
              {fines.length} {fines.length === 1 ? "bøde" : "bøder"}
            </div>
          )}
        </div>
        {isLoading ? (
          <Skeleton className="w-24 h-[38px] mt-1 bg-white/15" />
        ) : (
          <FadeIn enabled={animateIn} className="font-display font-extrabold text-[38px] leading-none tracking-tight mt-1 tabular-nums">
            {totalAmount}
            <span className="font-mono text-[13px] font-medium text-white/55 ml-1">KR</span>
          </FadeIn>
        )}

        {!isLoading && totalAmount > 0 && (
          <FadeIn enabled={animateIn} delay={0.05} className="mt-4">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden flex">
              <div className="h-full bg-vote-yes rounded-full" style={{ width: `${paidShare * 100}%` }} />
            </div>
            <div className="mt-2.5 flex items-center justify-between font-mono text-[10px] tracking-wide uppercase tabular-nums">
              <span className="inline-flex items-center gap-1.5 text-white/70">
                <span className="w-[6px] h-[6px] rounded-full bg-vote-yes" />
                Betalt {paidAmount} kr
              </span>
              {unpaidAmount > 0 ? (
                <span className="inline-flex items-center gap-1.5 text-white/70">
                  <span className="w-[6px] h-[6px] rounded-full bg-white/25" />
                  Udestående {unpaidAmount} kr
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-white/70">
                  <Icon name="check" size={10} />
                  Alt betalt
                </span>
              )}
            </div>
          </FadeIn>
        )}
      </div>

      {isLoading ? (
        <div className="mt-7 pb-4">
          <Skeleton className="w-40 h-3 mb-3" />
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[148px] rounded-lg border border-line-soft bg-card p-[18px] flex gap-4">
                <Skeleton className="w-11 h-11 rounded-full" />
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between">
                    <Skeleton className="w-24 h-4" />
                    <Skeleton className="w-12 h-5" />
                  </div>
                  <Skeleton className="w-full h-3 mt-2.5" />
                  <Skeleton className="w-2/3 h-3 mt-2" />
                  <Skeleton className="w-28 h-6 rounded-full mt-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : fines.length === 0 ? (
        <FadeIn enabled={animateIn} className="mt-6 mb-5 py-10 px-6 rounded-lg border border-dashed border-line flex flex-col items-center text-center">
          <div className="w-11 h-11 rounded-full bg-paper2 text-ink3 flex items-center justify-center">
            <Icon name="gavel" size={17} />
          </div>
          <div className="font-display text-base font-bold uppercase tracking-tight mt-3">Ingen bøder endnu</div>
          <div className="text-[13px] text-ink3 mt-1">Sæsonen er ren. Indtil videre.</div>
        </FadeIn>
      ) : (
        <Stagger enabled={animateIn} className="mt-7 flex flex-col gap-7 pb-5">
          {groups.map((group) => (
            <StaggerItem key={group.day}>
              <section aria-label={dayLabel(group.day)}>
                <div className="flex items-baseline justify-between gap-3 px-1 mb-3 font-mono text-[10px] tracking-[1.4px] uppercase text-ink3 tabular-nums">
                  <span className="font-semibold text-ink2">{dayLabel(group.day)}</span>
                  <span>
                    {group.fines.length} {group.fines.length === 1 ? "bøde" : "bøder"} · {group.total} kr
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  {group.fines.map((fine) => (
                    <FineRow
                      key={fine.id}
                      fine={fine}
                      canEdit={canEdit}
                      onMarkPaid={() => setMarkingFine(fine)}
                      onClick={() => setDetailFine(fine)}
                    />
                  ))}
                </div>
              </section>
            </StaggerItem>
          ))}
        </Stagger>
      )}

      {canEdit ? (
        // Pinned to the bottom of the scroll view rather than parked after the
        // list: fines get handed out mid-meeting, and with a season's worth
        // above it the old full-width button was a long scroll away. Sticky
        // (not fixed) keeps it in flow, so it still takes up its own space
        // under the last row and never covers it at the end of the list.
        <div className="sticky bottom-0 z-10 flex justify-end pt-1 pb-4 pointer-events-none">
          <button
            onClick={() => navigate("/fines/add")}
            className="pointer-events-auto h-12 pl-4 pr-5 rounded-full bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center gap-2 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.18)] active:scale-95 transition-transform"
          >
            <Icon name="plus" size={16} />
            Tilføj bøde
          </button>
        </div>
      ) : (
        <div className="pb-5 pt-3 border-t border-line-soft flex items-center justify-center gap-2 font-mono text-[10px] tracking-wide uppercase text-ink3">
          <Icon name="lock" size={11} />
          <span>Kun Skatteministeren kan rette</span>
        </div>
      )}

      {markingFine && (
        <MarkPaidSheet
          fine={markingFine}
          isSubmitting={isMarking}
          onConfirm={onConfirmPaid}
          onCancel={() => setMarkingFine(null)}
        />
      )}

      <FineDetailSheet
        fine={detailFine}
        canEdit={canEdit}
        onClose={() => setDetailFine(null)}
        onMarkPaid={(fine) => {
          setDetailFine(null);
          setMarkingFine(fine);
        }}
        onEdit={(fine) => navigate(`/fines/${fine.id}`)}
      />

      <BankInfoSheet open={bankInfoOpen} onClose={() => setBankInfoOpen(false)} />
    </div>
  );
};

export default ListPenaltiesPage;
